export function format_date(dateInput) {
  if (!dateInput) return "N/A";

  let date;
  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === "number") {
    // Handle timestamps
    date = new Date(dateInput);
  } else if (typeof dateInput === "string") {
    // Handle ISO or other string formats
    date = new Date(dateInput);
  } else {
    return "N/A";
  }

  if (isNaN(date.getTime())) {
    return "N/A";
  }

  // Format: Month DD, YYYY (e.g., May 12, 2025)
  return date.toLocaleDateString("en-GB", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function format_date_time(dateInput) {
  if (!dateInput) return "N/A";

  let date;
  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === "number") {
    date = new Date(dateInput);
  } else if (typeof dateInput === "string") {
    date = new Date(dateInput);
  } else {
    return "N/A";
  }

  if (isNaN(date.getTime())) {
    return "N/A";
  }

  const datePart = date.toLocaleDateString("en-GB", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${datePart} ${hours}:${minutes}`;
}

/**
 * Converts a date value to YYYY-MM-DD format for API/backend consumption.
 * Handles multiple input formats:
 * - YYYYMMDD (e.g., "20150514" -> "2015-05-14")
 * - DD/MM/YYYY (e.g., "14/05/2015" -> "2015-05-14")
 * - MM-DD-YYYY (e.g., "05-14-2015" -> "2015-05-14")
 * - Date objects
 * - ISO strings
 * 
 * @param {string|Date|null} value - The date value to format
 * @returns {string|null} - Date in YYYY-MM-DD format or null if invalid/empty
 */
export function format_date_for_api(value) {
  if (!value) return null;
  
  // Handle empty strings
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  // If already in correct format (YYYY-MM-DD), return as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // Handle YYYYMMDD format (e.g., "20150514" -> "2015-05-14")
  if (typeof value === 'string' && /^\d{8}$/.test(value)) {
    const year = value.substring(0, 4);
    const month = value.substring(4, 6);
    const day = value.substring(6, 8);
    return `${year}-${month}-${day}`;
  }

  // Handle DD/MM/YYYY format (e.g., "14/05/2015" -> "2015-05-14")
  if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const parts = value.split('/');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  // Handle MM-DD-YYYY format (e.g., "05-14-2015" -> "2015-05-14")
  if (typeof value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const parts = value.split('-');
    return `${parts[2]}-${parts[0]}-${parts[1]}`;
  }

  // Try to parse as a Date object for other formats
  try {
    const dateObj = value instanceof Date ? value : new Date(value);
    if (!isNaN(dateObj.getTime())) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {
    console.warn('Could not parse date value:', value);
  }

  return null;
}

/**
 * Formats date fields in an object to YYYY-MM-DD format for API/backend consumption.
 * 
 * @param {Object} data - The data object containing date fields
 * @param {string[]} dateFields - Array of field names that should be treated as dates (required)
 * @returns {Object} - New object with formatted date fields
 */
export function format_date_fields_for_api(data, dateFields = []) {
  if (!data || typeof data !== 'object') return data;
  if (!dateFields || dateFields.length === 0) return data;

  const formattedData = { ...data };

  dateFields.forEach((fieldName) => {
    if (fieldName in formattedData) {
      formattedData[fieldName] = format_date_for_api(formattedData[fieldName]);
    }
  });

  return formattedData;
}
